import axios from 'axios';

import { authBaseInstance, authBetProcessor, authContent, publicBaseInstance } from '@/lib/api';
import { logger } from '@/lib/api/logger';
import { core } from '@/config';
import { customTournamentPageStats } from '@/config/constants';

/**
 * @template T
 * @typedef {import('axios').AxiosPromise<T>} AxiosPromise
 */

/**
 * get custom tournament page statistics data
 * @param {string} type - tab for statistics
 */
export function getCustomTournamentPageStats(type) {
  if (!customTournamentPageStats) {
    logger.warn(
      'You are trying to make a request without specifying the customTournamentPageStats constant',
    );
    return [];
  }
  return axios.get(`${core}/api/${customTournamentPageStats}/${type}`);
}

/**
 * get schedule by discipline id
 * @param {string} disciplineId - discipline id
 * @param {Object} params - params
 *
 * @returns {AxiosPromise<{data: EP.Event[]}>}
 */
export function getScheduleByDid(disciplineId, params = {}) {
  return authBetProcessor.get(`/schedule/${disciplineId}`, { params });
}

/**
 * get events by discipline id
 * @param {Object} params - params
 *
 * @returns {AxiosPromise<{data: PublicEP.ResultedEvent}>}
 */
export function getStatisticsEventsByDid(params = {}) {
  return authContent.get(`/results`, { params });
}

/**
 * get settled event statistics by event id
 * @param {string} eventId - event id
 * @param {string} locale - locale
 *
 * @returns {AxiosPromise<{data: PublicEP.Event}>}
 */
export function getSettledEventStats(eventId, locale) {
  return authBetProcessor.get(`/events/${eventId}`, { params: { locale } });
}

/**
 * get settled event markets by event id
 * @param {string} eventId - event id
 * @param {string} locale - locale
 *
 * @returns {AxiosPromise<{data: PublicEP.Market[]}>}
 */
export function getSettledEventMarkets(eventId, locale) {
  return authBetProcessor.get(`/events/${eventId}/markets`, { params: { locale } });
}

/**
 * get settled event scoreboards by event id
 * @param {string} eventId - event id
 *
 * @returns {AxiosPromise<{data: PublicEP.Scoreboard}>}
 */
export function getSettledEventScoreboards(eventId) {
  return authBetProcessor.get(`/events/${eventId}/scoreboard`);
}

/**
 * get events ids for 'custom events page'
 *
 * @returns {AxiosPromise<{data: string[]}>}
 */
export function getCustomEventsIds() {
  return publicBaseInstance.get('/api/experimental/custom-events/eventIds');
}

/**
 * get intervals by discipline id
 * @param {string} disciplineId - discipline id
 * @param {Object} params - params
 *
 * @returns {AxiosPromise<{data: {}}>}
 */
export function getIntervalsByDiscipline(disciplineId, params = {}) {
  return authContent.get(`/intervals/${disciplineId}`, { params });
}

/**
 * add or remove tournament from favorites
 * @param {{[key: string]: any}} params - request params
 * @param {{slug?: string; remove?: boolean}} opts - request options
 *
 * @returns {AxiosPromise<{data: { url: string }}>}
 */
export function setFavorites(params, opts = {}) {
  let { slug = 'tournament', remove = false } = opts;
  slug = slug === 'tournaments' ? 'tournament' : slug;

  if (remove) {
    return authBaseInstance.delete(`/public/api/profile/favorite/${slug}`, {
      data: params,
    });
  }
  return authBaseInstance.post(`/public/api/profile/favorite/${slug}`, params);
}

/**
 * @param {string} eventId
 */
export function getTokenForDataBetWidgets(eventId) {
  return authContent.get(`/events/${eventId}/widgets`);
}
